import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';
import './i18n';
import './index.css';
import { isApiError } from './lib/api';
import { createAppRouter } from './router';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(isApiError(err) && err.status < 500) && count < 2,
      staleTime: 15_000,
    },
  },
  queryCache: new QueryCache({ onError: (err) => handleUnauthenticated(err) }),
  mutationCache: new MutationCache({ onError: (err) => handleUnauthenticated(err) }),
});

const router = createAppRouter(queryClient);

/** A 401 anywhere means the session ended (logout elsewhere, deactivation, expiry). */
function handleUnauthenticated(err: unknown) {
  if (
    isApiError(err) &&
    err.status === 401 &&
    !router.state.location.pathname.startsWith('/login')
  ) {
    queryClient.clear();
    void router.navigate({ to: '/login', search: { redirect: router.state.location.href } });
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster position="bottom-right" richColors closeButton />
    </QueryClientProvider>
  </StrictMode>,
);
